export interface MosaicTemplateSlot {
    /** Unique id for this slot within the template */
    id: string;
    /** Normalized rect (0-1) — must be grid-snapped */
    rect: { x: number; y: number; w: number; h: number };
}

export interface MosaicTemplate {
    /** Display name for the menu */
    name: string;
    /** The slots that make up this template */
    slots: MosaicTemplateSlot[];
}

/**
 * Column Focus Grid: left panel (40%) + right column of 3 rows (30/30/40%).
 * All values pre-snapped to MOSAIC_GRID_INCREMENT (0.02).
 */
export const TEMPLATE_COLUMN_FOCUS: MosaicTemplate = {
    name: 'Column Focus Grid',
    slots: [
        { id: 'left', rect: { x: 0, y: 0, w: 0.4, h: 1.0 } },
        { id: 'right-top', rect: { x: 0.4, y: 0, w: 0.6, h: 0.3 } },
        { id: 'right-middle', rect: { x: 0.4, y: 0.3, w: 0.6, h: 0.3 } },
        { id: 'right-bottom', rect: { x: 0.4, y: 0.6, w: 0.6, h: 0.4 } },
    ],
};

/**
 * 2x2 Grid: four equal quadrants.
 * All values pre-snapped to MOSAIC_GRID_INCREMENT (0.02).
 */
export const TEMPLATE_2X2: MosaicTemplate = {
    name: '2x2 Grid',
    slots: [
        { id: 'top-left', rect: { x: 0, y: 0, w: 0.5, h: 0.5 } },
        { id: 'top-right', rect: { x: 0.5, y: 0, w: 0.5, h: 0.5 } },
        { id: 'bottom-left', rect: { x: 0, y: 0.5, w: 0.5, h: 0.5 } },
        { id: 'bottom-right', rect: { x: 0.5, y: 0.5, w: 0.5, h: 0.5 } },
    ],
};

/**
 * 3x3 Grid: nine equal cells.
 * All values pre-snapped to MOSAIC_GRID_INCREMENT (0.02).
 */
export const TEMPLATE_3X3: MosaicTemplate = {
    name: '3x3 Grid',
    slots: [
        { id: 'r0-c0', rect: { x: 0, y: 0, w: 0.34, h: 0.34 } },
        { id: 'r0-c1', rect: { x: 0.34, y: 0, w: 0.32, h: 0.34 } },
        { id: 'r0-c2', rect: { x: 0.66, y: 0, w: 0.34, h: 0.34 } },
        { id: 'r1-c0', rect: { x: 0, y: 0.34, w: 0.34, h: 0.32 } },
        { id: 'r1-c1', rect: { x: 0.34, y: 0.34, w: 0.32, h: 0.32 } },
        { id: 'r1-c2', rect: { x: 0.66, y: 0.34, w: 0.34, h: 0.32 } },
        { id: 'r2-c0', rect: { x: 0, y: 0.66, w: 0.34, h: 0.34 } },
        { id: 'r2-c1', rect: { x: 0.34, y: 0.66, w: 0.32, h: 0.34 } },
        { id: 'r2-c2', rect: { x: 0.66, y: 0.66, w: 0.34, h: 0.34 } },
    ],
};

const MOSAIC_TEMPLATES: Record<string, MosaicTemplate> = {
    'column-focus': TEMPLATE_COLUMN_FOCUS,
    '2x2': TEMPLATE_2X2,
    '3x3': TEMPLATE_3X3,
};

export function getMosaicTemplateById(id: string): MosaicTemplate | undefined {
    return MOSAIC_TEMPLATES[id];
}

export function getAllMosaicTemplates(): Record<string, MosaicTemplate> {
    return MOSAIC_TEMPLATES;
}
